import { DateAdapter } from '@angular/material/core';
import * as jalaali from 'jalaali-js';

export const PERSIAN_DATE_FORMATS = {
  parse: {
    dateInput: 'jYYYY/jMM/jDD'
  },
  display: {
    dateInput: 'jYYYY/jMM/jDD',
    monthYearLabel: 'jMMMM jYYYY',
    dateA11yLabel: 'jYYYY/jMM/jDD',
    monthYearA11yLabel: 'jMMMM jYYYY',
  }
};

export class MaterialPersianDateAdapter extends DateAdapter<Date> {
  constructor() {
    super();
    super.setLocale('fa');
  }

  override getYear(date: Date): number {
    return jalaali.toJalaali(date).jy;
  }

  override getMonth(date: Date): number {
    return jalaali.toJalaali(date).jm - 1; // jalaali months are 1-based
  }

  override getDate(date: Date): number {
    return jalaali.toJalaali(date).jd;
  }

  override getDayOfWeek(date: Date): number {
    return date.getDay();
  }

  override getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    return [
      'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
      'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
    ];
  }

  override getDateNames(): string[] {
    const names: string[] = [];
    for (let i = 1; i <= 31; i++) {
      names.push(String(i));
    }
    return names;
  }

  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    return ['ی', 'د', 'س', 'چ', 'پ', 'ج', 'ش'];
  }

  override getYearName(date: Date): string {
    return String(this.getYear(date));
  }

  override getFirstDayOfWeek(): number {
    return 6; // Saturday
  }

  override getNumDaysInMonth(date: Date): number {
    const { jy, jm } = jalaali.toJalaali(date);
    return jalaali.jalaaliMonthLength(jy, jm);
  }

  override clone(date: Date): Date {
    return new Date(date.getTime());
  }

  override createDate(jy: number, jm: number, jd: number): Date {
    if (jm < 0 || jm > 11) throw Error(`ماه نامعتبر: ${jm}`);
    if (jd < 1 || jd > 31) throw Error(`روز نامعتبر: ${jd}`);

    const { gy, gm, gd } = jalaali.toGregorian(jy, jm + 1, jd);
    const result = new Date(Date.UTC(gy, gm - 1, gd));
    if (isNaN(result.getTime())) throw Error(`تاریخ نامعتبر: ${jy}/${jm + 1}/${jd}`);
    return result;
  }

  override today(): Date {
    return new Date();
  }

  override parse(value: any, parseFormat: string | string[]): Date | null {
    if (typeof value === 'string') {
      const parts = value.split('/');
      if (parts.length === 3) {
        const [jy, jm, jd] = parts.map(Number);
        return this.createDate(jy, jm - 1, jd);
      }
    } else if (value instanceof Date) {
      return new Date(value.getTime());
    }
    return null;
  }

  override format(date: Date, displayFormat: string): string {
    const { jy, jm, jd } = jalaali.toJalaali(date);
    const monthNames = this.getMonthNames('long');
    const monthName = monthNames[jm - 1];

    switch (displayFormat) {
      case 'jMMMM jYYYY':
        return `${monthName} ${jy}`;
      case 'jYYYY/jMM/jDD':
      default:
        return `${jy}/${this._2digit(jm)}/${this._2digit(jd)}`;
    }
  }

  override addCalendarYears(date: Date, years: number): Date {
    const { jy, jm, jd } = jalaali.toJalaali(date);
    return this.createDate(jy + years, jm - 1, jd);
  }

  override addCalendarMonths(date: Date, months: number): Date {
    const jy = this.getYear(date);
    const jm0 = this.getMonth(date);
    const { jd } = jalaali.toJalaali(date);

    let monthIndex = jm0 + months;
    let newYear = jy + Math.floor(monthIndex / 12);
    monthIndex = ((monthIndex % 12) + 12) % 12;

    return this.createDate(newYear, monthIndex, jd);
  }


  override addCalendarDays(date: Date, days: number): Date {
    const result = new Date(date.getTime());
    result.setUTCDate(result.getUTCDate() + days);
    return result;
  }

  override toIso8601(date: Date): string {
    return date.toISOString();
  }

  override isDateInstance(obj: any): boolean {
    return obj instanceof Date;
  }

  override isValid(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  override invalid(): Date {
    return new Date(NaN);
  }

  override deserialize(value: any): Date | null {
    if (typeof value === 'string') {
      return this.parse(value, '');
    } else if (value instanceof Date) {
      return this.clone(value);
    }
    return null;
  }

  private _2digit(n: number): string {
    return n < 10 ? '0' + n : String(n);
  }
}